/**
 * Head tags on an institute's own domain (and its subdomain).
 *
 * RouteBasedSEO writes EduExpertAI's marketing head (title, description,
 * canonical to eduexpertai.com, "index, follow", our JSON-LD) on every route.
 * On a customer's domain that overwrote the institute's own head, which the
 * server had sent (sitebuilder/seo.build_head), for every crawler that runs
 * JavaScript and in every open tab. Here nothing of ours is written:
 *
 *   title        "Sign in | <name>" on account pages, else "<name>"
 *   description  the institute's tagline, else its name
 *   robots       noindex: the portal is not a page to list (the website is,
 *                and its own pages set their head themselves)
 *   og/twitter   the institute's name and logo
 *   removed      keywords, author, canonical, our JSON-LD
 *
 * Website pages (features/site PublicSite) set their own head with Helmet and
 * are skipped, so the two never fight over the same tags.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useBranding } from '@/contexts/BrandingContext';

const ACCOUNT_TITLES: Record<string, string> = {
  '/login': 'Sign in',
  '/2fa': 'Two-step sign-in',
  '/forgot-password': 'Forgot password',
  '/join': 'Request to join',
};

/** Routes whose head the public website sets itself. */
export const isWebsiteRoute = (path: string): boolean => path.startsWith('/institutes/');

const setMeta = (attr: 'name' | 'property', key: string, value: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!value) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
  tag.setAttribute(TENANT_SEO_ATTR, '1');
};

/** Marks the tags written here, so the website can clear them when it shows. */
export const TENANT_SEO_ATTR = 'data-tenant-seo';

/** Pure, for tests: the tags for one route. */
export const tenantHead = (path: string, branding: { appName?: string; shortName?: string; tagline?: string; logoUrl?: string }) => {
  const name = branding.appName || branding.shortName || '';
  const sub = ACCOUNT_TITLES[path.replace(/\/+$/, '') || '/'];
  return {
    title: name ? (sub ? `${sub} | ${name}` : name) : sub || '',
    description: branding.tagline || name,
    image: branding.logoUrl || '',
    name,
  };
};

const TenantSEO: React.FC = () => {
  const { pathname } = useLocation();
  const { branding, loading } = useBranding();

  useEffect(() => {
    // A website page is showing (PublicSite sets the flag and clears our tags):
    // its own, indexable head wins.
    if (loading || isWebsiteRoute(pathname) || document.documentElement.dataset.siteHead) return;
    const head = tenantHead(pathname, branding);
    if (head.title) document.title = head.title;
    setMeta('name', 'description', head.description);
    setMeta('name', 'robots', 'noindex, nofollow');
    setMeta('name', 'keywords', '');
    setMeta('name', 'author', '');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', head.name);
    setMeta('property', 'og:title', head.title);
    setMeta('property', 'og:description', head.description);
    setMeta('property', 'og:image', head.image);
    setMeta('property', 'og:url', '');
    setMeta('name', 'twitter:card', head.image ? 'summary' : '');
    setMeta('name', 'twitter:title', head.title);
    setMeta('name', 'twitter:description', head.description);
    setMeta('name', 'twitter:image', head.image);
    document.head.querySelectorAll('link[rel="canonical"], script[type="application/ld+json"]').forEach((n) => n.remove());
  }, [pathname, branding, loading]);

  return null;
};

export default TenantSEO;
